import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FolderOpen, CheckCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import { fileExplorerService } from '../../../services/files/fileExplorerService.js';
import { HttpError } from '../../../services/http/httpClient.js';
import FileExplorerBreadcrumb from '../components/FileExplorerBreadcrumb.jsx';
import FileExplorerToolbar from '../components/FileExplorerToolbar.jsx';
import FileExplorerTable from '../components/FileExplorerTable.jsx';
import FileExplorerGrid from '../components/FileExplorerGrid.jsx';
import FileExplorerErrorState from '../components/FileExplorerErrorState.jsx';
import FileExplorerSelectionBar from '../components/FileExplorerSelectionBar.jsx';
import FileExplorerMaintenanceModal from '../components/FileExplorerMaintenanceModal.jsx';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog.jsx';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';

const DEFAULT_SORT_BY = 'size';
const DEFAULT_SORT_ORDER = 'desc';
const DEFAULT_PAGE_SIZE = 5;

const mergeUniqueEntries = (currentEntries, newEntries) => {
  const entriesById = new Map(currentEntries.map((entry) => [entry.id, entry]));
  newEntries.forEach((entry) => entriesById.set(entry.id, entry));
  return Array.from(entriesById.values());
};

const mapErrorToState = (err) => {
  if (err instanceof HttpError && err.status === 404) {
    return { kind: 'not_found' };
  }

  if (err instanceof HttpError && err.status === 403) {
    return { kind: 'forbidden' };
  }

  return { kind: 'server' };
};

const FileExplorerScreen = ({ userRole }) => {
  const canManageFiles = userRole === 'admin';
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folder, setFolder] = useState(null);
  // Construido localmente a partir de los clicks del usuario, no del campo
  // `breadcrumb` de la respuesta: el backend lo devuelve siempre vacío ([])
  // incluso dentro de una carpeta de tarea, pese a lo documentado en el
  // contrato — ver nota en implementation-plan.md. Cada entrada guarda
  // { id, name } de la carpeta clickeada, en orden de navegación.
  const [breadcrumbTrail, setBreadcrumbTrail] = useState([]);
  const [entries, setEntries] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [search, setSearch] = useState('');
  const [pageCurrent, setPageCurrent] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  // Tamaño de página real que aplicó el backend a la primera tanda de la
  // grilla (viene de la respuesta, no se hardcodea en el front: el tope de
  // 20 es una decisión del backend y podría cambiar sin aviso). Se usa para
  // pedir las tandas siguientes con el mismo tamaño.
  const [gridPageSize, setGridPageSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMoreGrid, setLoadingMoreGrid] = useState(false);
  const [error, setError] = useState(null);

  // Map<id, entry> en vez de un array/Set de ids: para armar el body de
  // DELETE/download hace falta type/source de cada entry, y para el mensaje
  // de confirmación el fileCount de las carpetas — todo eso ya vino en el
  // entry con el listado, no hay que volver a pedirlo.
  const [selectedEntries, setSelectedEntries] = useState(() => new Map());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const fetchFolder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fileExplorerService.getFileExplorer({
        folderId: currentFolderId ?? undefined,
        sortBy,
        sortOrder,
        search: search || undefined,
        // La grilla no muestra controles de paginación, pero el backend igual
        // pagina (aunque se omitan estos parámetros): se pide la primera
        // tanda sin fijar un tamaño; el resto se trae con handleLoadMoreGrid,
        // concatenando en vez de reemplazar.
        ...(viewMode === 'list' ? { pageCurrent, pageSize } : {})
      });

      const responses = response.responses || [];
      setFolder(response.folder);
      setEntries(responses);
      setTotalItems(response.pagination?.totalItems || 0);
      if (viewMode === 'grid') {
        setGridPageSize(response.pagination?.pageSize || responses.length || null);
      }
    } catch (err) {
      setEntries([]);
      setTotalItems(0);
      setError(mapErrorToState(err));
    } finally {
      setLoading(false);
    }
  }, [currentFolderId, sortBy, sortOrder, search, pageCurrent, pageSize, viewMode]);

  useEffect(() => {
    fetchFolder();
  }, [fetchFolder]);

  // pageCurrent se deduce de `entries.length` sobre el tamaño de página real
  // (gridPageSize, conocido recién tras la primera respuesta) en vez de
  // llevar un contador de página aparte que se pueda desincronizar.
  const handleLoadMoreGrid = async () => {
    if (loadingMoreGrid || !gridPageSize) return;

    setLoadingMoreGrid(true);
    try {
      const nextPageCurrent = Math.floor(entries.length / gridPageSize);
      const response = await fileExplorerService.getFileExplorer({
        folderId: currentFolderId ?? undefined,
        sortBy,
        sortOrder,
        search: search || undefined,
        pageCurrent: nextPageCurrent,
        pageSize: gridPageSize
      });

      setEntries((prev) => mergeUniqueEntries(prev, response.responses || []));
      setTotalItems(response.pagination?.totalItems || 0);
    } catch (err) {
      setError(mapErrorToState(err));
    } finally {
      setLoadingMoreGrid(false);
    }
  };

  const handleFolderClick = (entry) => {
    setPageCurrent(0);
    setSelectedEntries(new Map());
    setBreadcrumbTrail((prev) => [...prev, { id: entry.id, name: entry.name }]);
    setCurrentFolderId(entry.id);
  };

  // En modo selección, clickear una carpeta selecciona en vez de navegar —
  // evita el caso reportado de terminar dentro de una subcarpeta al intentar
  // tildar varios elementos.
  const handleEntryRowClick = (entry) => {
    if (isSelectionMode) {
      handleToggleEntry(entry);
      return;
    }
    handleFolderClick(entry);
  };

  const handleBreadcrumbClick = (crumbId) => {
    setPageCurrent(0);
    setSelectedEntries(new Map());
    if (crumbId === null) {
      setBreadcrumbTrail([]);
    } else {
      setBreadcrumbTrail((prev) => {
        const index = prev.findIndex((crumb) => crumb.id === crumbId);
        return index === -1 ? prev : prev.slice(0, index + 1);
      });
    }
    setCurrentFolderId(crumbId);
  };

  // "Seleccionar todo" solo actúa sobre `entries` (lo cargado/visible en este
  // momento — la página actual en list, lo acumulado por "Cargar más" en
  // grid), no sobre todo el contenido de la carpeta.
  const selectedIds = useMemo(() => new Set(selectedEntries.keys()), [selectedEntries]);
  const allVisibleSelected = entries.length > 0 && entries.every((entry) => selectedEntries.has(entry.id));

  const handleToggleEntry = (entry) => {
    setSelectedEntries((prev) => {
      const next = new Map(prev);
      if (next.has(entry.id)) {
        next.delete(entry.id);
      } else {
        next.set(entry.id, entry);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedEntries((prev) => {
      const next = new Map(prev);
      if (allVisibleSelected) {
        entries.forEach((entry) => next.delete(entry.id));
      } else {
        entries.forEach((entry) => next.set(entry.id, entry));
      }
      return next;
    });
  };

  const handleClearSelection = () => setSelectedEntries(new Map());

  // El toggle es la única puerta de entrada/salida del modo selección: al
  // salir se limpia la selección para que no quede "colgada" la próxima vez
  // que se active (p. ej. tras cambiar de carpeta).
  const handleToggleSelectionMode = () => {
    setIsSelectionMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedEntries(new Map());
      }
      return next;
    });
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  const mapActionErrorToMessage = (err, accion) => {
    if (err instanceof HttpError) {
      if (err.status === 403) {
        return 'No tienes permiso para realizar esta acción.';
      }
      if (err.status === 404) {
        return 'Alguno de los elementos seleccionados ya no existe. Actualizando la lista...';
      }
      if (err.status === 400) {
        return `No se pudo ${accion}: la selección es inválida o supera el límite permitido por operación.`;
      }
    }
    return `Ocurrió un error inesperado al intentar ${accion}. Inténtalo de nuevo.`;
  };

  const selectedEntryList = useMemo(() => Array.from(selectedEntries.values()), [selectedEntries]);
  const estimatedFileCount = useMemo(
    () => selectedEntryList.reduce((sum, entry) => sum + (entry.type === 'folder' ? (entry.fileCount || 0) : 1), 0),
    [selectedEntryList]
  );

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const result = await fileExplorerService.deleteFileExplorerItems(selectedEntryList);
      const deletedCount = result?.deleted?.length || 0;
      const failedCount = result?.failed?.length || 0;

      if (failedCount > 0) {
        showToast(`Se eliminaron ${deletedCount} elemento(s); ${failedCount} no se pudo(eron) eliminar.`, 'warning');
      } else {
        showToast(`Se eliminaron ${deletedCount} elemento(s) correctamente.`, 'success');
      }

      setSelectedEntries(new Map());
      setShowDeleteConfirm(false);

      if (pageCurrent !== 0) {
        setPageCurrent(0);
      } else {
        fetchFolder();
      }
    } catch (err) {
      showToast(mapActionErrorToMessage(err, 'eliminar'), 'error');
      setShowDeleteConfirm(false);
      if (err instanceof HttpError && err.status === 404) {
        fetchFolder();
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadSelected = async () => {
    setDownloading(true);
    try {
      const response = await fileExplorerService.downloadFileExplorerItems(selectedEntryList);
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'archivos.zip';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err instanceof HttpError) {
        const payload = await err.response?.json().catch(() => null);
        showToast(payload?.message || mapActionErrorToMessage(err, 'descargar'), 'error');
        if (err.status === 404) {
          fetchFolder();
        }
      } else {
        // La conexión se cortó a mitad del stream del zip: se trata como
        // descarga fallida, nunca se dispara el <a download> con un blob truncado.
        showToast('La descarga se interrumpió. Inténtalo de nuevo.', 'error');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setPageCurrent(0);
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  };

  const handleSearchChange = (term) => {
    setPageCurrent(0);
    setSearch(term);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Explorador de archivos"
        subtitle="Navega y busca los archivos almacenados en las tareas"
        icon={<FolderOpen size={28}/>}
      />
      <FileExplorerToolbar
        scopeName={folder?.name || 'Raíz'}
        sortBy={sortBy}
        sortOrder={sortOrder}
        resetKey={currentFolderId}
        viewMode={viewMode}
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        onViewModeChange={setViewMode}
        canSelect={canManageFiles}
        selectionMode={isSelectionMode}
        onToggleSelectionMode={handleToggleSelectionMode}
        onOpenMaintenance={() => setShowMaintenanceModal(true)}
      />
      <FileExplorerBreadcrumb items={breadcrumbTrail} onNavigate={handleBreadcrumbClick} />

      {canManageFiles && isSelectionMode && !error && (
        <FileExplorerSelectionBar
          totalVisible={entries.length}
          selectedCount={selectedEntries.size}
          allVisibleSelected={allVisibleSelected}
          onToggleSelectAll={handleToggleSelectAll}
          onClear={handleToggleSelectionMode}
          onDelete={() => setShowDeleteConfirm(true)}
          onDownload={handleDownloadSelected}
          deleting={deleting}
          downloading={downloading}
        />
      )}

      {error ? (
        <FileExplorerErrorState
          kind={error.kind}
          onAction={error.kind === 'not_found' ? () => handleBreadcrumbClick(null) : fetchFolder}
        />
      ) : viewMode === 'grid' ? (
        <FileExplorerGrid
          entries={entries}
          loading={loading}
          search={search}
          onRowClick={handleEntryRowClick}
          totalItems={totalItems}
          hasMore={entries.length < totalItems}
          loadingMore={loadingMoreGrid}
          onLoadMore={handleLoadMoreGrid}
          selectable={canManageFiles && isSelectionMode}
          selectedIds={selectedIds}
          onToggleEntry={handleToggleEntry}
        />
      ) : (
        <FileExplorerTable
          entries={entries}
          loading={loading}
          search={search}
          onRowClick={handleEntryRowClick}
          selectable={canManageFiles && isSelectionMode}
          selectedIds={selectedIds}
          onToggleEntry={handleToggleEntry}
          pagination={{
            currentPage: pageCurrent + 1,
            totalPages,
            pageSize,
            totalItems,
            pageSizeOptions: [5, 10, 20],
            onPageChange: (page) => setPageCurrent(page - 1),
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPageCurrent(0);
            }
          }}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Eliminar elementos"
        message={`¿Eliminar ${selectedEntryList.length} elemento(s) seleccionado(s)? Esto afecta aproximadamente ${estimatedFileCount} archivo(s) — el contenido de las carpetas seleccionadas se elimina por completo. Esta acción no se puede deshacer.`}
        confirmText={deleting ? 'Eliminando...' : 'Eliminar'}
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <FileExplorerMaintenanceModal
        isOpen={showMaintenanceModal}
        onClose={() => setShowMaintenanceModal(false)}
        onDeleted={() => {
          setSelectedEntries(new Map());
          fetchFolder();
        }}
        onNotify={showToast}
      />

      {toast.show && (
        <div
          className={`fixed top-4 right-4 z-10000 p-4 rounded-lg shadow-lg ${
            toast.type === 'success'
              ? 'bg-success/12 border border-success text-success'
              : toast.type === 'warning'
                ? 'bg-warning/12 border border-warning text-warning'
                : 'bg-error/12 border border-error text-error'
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === 'success' ? (
              <CheckCircle size={20} weight="bold" />
            ) : toast.type === 'warning' ? (
              <WarningCircle size={20} weight="bold" />
            ) : (
              <XCircle size={20} weight="bold" />
            )}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileExplorerScreen;
